import { type JSX, Show, createSignal } from 'solid-js';
import { CalendarDays, ChevronDown } from 'lucide-solid';
import { cn } from '~/shared/lib/cn';
import { useClubClock } from '~/shared/lib/useClubClock';
import { Calendar } from './Calendar';
import { Popover } from './Popover';

export const formatDateValue = (iso: string): string => {
  if (!iso) return '';
  return new Date(`${iso}T00:00:00`)
    .toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })
    .replace(/\sг\.?$/, '');
};

interface DatePickerProps {
  value: string;
  onChange: (iso: string) => void;
  min?: string;
  max?: string;
  disabled?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  size?: 'sm' | 'md';
  align?: 'start' | 'center' | 'end';
  class?: string;
  renderTrigger?: (ctx: {
    toggle: () => void;
    open: () => boolean;
    display: () => string;
    ref: (el: HTMLElement) => void;
  }) => JSX.Element;
}

export function DatePicker(props: DatePickerProps): JSX.Element {
  const [open, setOpen] = createSignal(false);
  const [anchor, setAnchor] = createSignal<HTMLElement>();

  const display = () => (props.value ? formatDateValue(props.value) : '');
  const toggle = () => {
    if (props.disabled) return;
    setOpen((o) => !o);
  };

  const pick = (iso: string) => {
    props.onChange(iso);
    setOpen(false);
  };


  return (
    <>
      <Show
        when={props.renderTrigger}
        fallback={
          <button
            type="button"
            ref={setAnchor}
            disabled={props.disabled}
            aria-label={props.ariaLabel ?? 'Выбрать дату'}
            aria-expanded={open()}
            onClick={toggle}
            class={cn(
              'press w-full rounded-md ring-hairline inline-flex items-center gap-2 text-left transition-colors disabled:opacity-50',
              props.size === 'sm' ? 'h-8 px-2.5 text-[12.5px]' : 'h-10 px-3 text-[14px]',
              props.class,
            )}
            style={{
              background: 'color-mix(in srgb, var(--color-bg-glass) 92%, transparent)',
              ...(open() ? { 'box-shadow': 'inset 0 0 0 1px var(--color-border-accent)' } : {}),
            }}
          >
            <CalendarDays class="h-4 w-4 shrink-0 text-tertiary" />
            <span class={cn('flex-1 truncate', props.value ? 'text-primary' : 'text-tertiary')}>
              {props.value ? display() : (props.placeholder ?? 'Выберите дату')}
            </span>
            <ChevronDown
              class={cn('h-4 w-4 shrink-0 text-tertiary transition-transform duration-fast', open() && 'rotate-180')}
            />
          </button>
        }
      >
        {(render) =>
          render()({
            toggle,
            open,
            display,
            ref: (el) => setAnchor(el),
          })
        }
      </Show>

      <Popover open={open()} onClose={() => setOpen(false)} anchor={anchor()} align={props.align ?? 'start'}>
        <Calendar
          mode="single"
          value={props.value}
          min={props.min}
          max={props.max}
          onPick={pick}
          footer={<TodayButton min={props.min} max={props.max} onPick={pick} />}
        />
      </Popover>
    </>
  );
}

export function DateField(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  min?: string;
  max?: string;
  disabled?: boolean;
  placeholder?: string;
}): JSX.Element {
  return (
    <label class="block">
      <span class="text-[10.5px] uppercase tracking-[0.10em] text-tertiary">{props.label}</span>
      <div class="mt-1">
        <DatePicker
          value={props.value}
          onChange={props.onChange}
          min={props.min}
          max={props.max}
          disabled={props.disabled}
          placeholder={props.placeholder}
          ariaLabel={props.label}
        />
      </div>
    </label>
  );
}

function TodayButton(props: {
  min?: string;
  max?: string;
  onPick: (iso: string) => void;
}): JSX.Element {
  const clock = useClubClock();
  const disabled = () => {
    const today = clock.today();
    return (props.min !== undefined && today < props.min) || (props.max !== undefined && today > props.max);
  };
  return (
    <button
      type="button"
      disabled={disabled()}
      onClick={() => props.onPick(clock.today())}
      class="press h-7 px-2.5 rounded-md text-[12px] font-medium text-secondary hover:bg-white/8 disabled:opacity-40"
    >
      Сегодня
    </button>
  );
}
